import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, apiClient } from '../App';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Home, Download, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import Footer from '../components/Footer';
import { useSafeBack } from '../hooks/useSafeBack';
import { toast } from 'sonner';

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '');
const EMPTY_VALUE = '\u2014';
const fmtPercent = (value, fallback = '0%') => {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? `${Math.round(n * 100)}%` : fallback;
};
const fmtOptionalPercent = (value) => fmtPercent(value, EMPTY_VALUE);
const fmtRosterStatus = (value) => {
  if (value === 'joined_unconfirmed') return 'Joined (unconfirmed)';
  if (value === 'unconfirmed_window_closed') return 'No response (window closed)';
  if (value === 'confirmed') return 'Confirmed';
  return value ? value.replace(/_/g, ' ') : EMPTY_VALUE;
};
const fmtHostRole = (value) => {
  if (value === 'primary_host') return 'Primary host';
  if (value === 'co_host') return 'Co-host';
  return EMPTY_VALUE;
};
const fmtAge = (value) => (Number.isInteger(value) ? value : 'Unknown');

const HostReports = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const { goBack } = useSafeBack('/host');

  const today = new Date();
  const ago30 = new Date(today.getTime() - 30 * 86400000);
  const toISO = (d) => d.toISOString().slice(0, 10);

  const [start, setStart] = useState(toISO(ago30));
  const [end, setEnd] = useState(toISO(today));
  const [includeUpcoming, setIncludeUpcoming] = useState(false);
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [showRosterDetail, setShowRosterDetail] = useState(true);
  const [clubId, setClubId] = useState('all');
  const [playerId, setPlayerId] = useState('all');
  const [hostRole, setHostRole] = useState('all');
  const [players, setPlayers] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    loadClubs();
    fetchReport();
  }, []);

  const loadClubs = async () => {
    try {
      const res = await apiClient.get('/clubs/mine');
      setClubs(res.data.clubs || []);
    } catch (e) { /* non-fatal */ }
  };

  const buildParams = () => {
    const p = { start, end, include_upcoming: includeUpcoming, include_cancelled: includeCancelled };
    if (clubId && clubId !== 'all') p.club_id = clubId;
    if (playerId && playerId !== 'all') p.player_id = playerId;
    if (hostRole && hostRole !== 'all') p.host_role = hostRole;
    return p;
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/host/roster-report', { params: buildParams() });
      setReport(res.data);
      // Keep the player candidate list stable (scoped to this host's data).
      // The API returns the full scoped list even when a player filter is applied.
      if (Array.isArray(res.data.players) && (playerId === 'all' || players.length === 0)) {
        setPlayers(res.data.players);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await apiClient.get('/host/roster-report', {
        params: { ...buildParams(), format: 'csv' },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `roster-report_${start}_${end}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('CSV export failed');
    }
  };

  const summary = report?.summary;
  const sessionRows = report?.session_summary || [];
  const rows = report?.rows || [];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="sticky top-0 z-30 bg-white border-b border-[#E4E4E7]"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
        <div className="mx-auto max-w-7xl px-4 pb-4 flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={goBack} className="h-10 w-10 rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="h-8 w-8 rounded-full" data-testid="nav-home-btn" aria-label="Dashboard">
            <Home className="w-4 h-4 text-zinc-400" />
          </Button>
          <h1 className="text-xl font-bold text-zinc-900 ml-1 flex items-center gap-2">
            <FileText className="w-5 h-5 text-orange-500" /> Host Reports
          </h1>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-6 space-y-6">
        {/* Filters */}
        <div className="bg-white rounded-xl border border-zinc-200 p-4" data-testid="report-filters">
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-7 gap-4 items-end">
            <div>
              <Label htmlFor="start" className="text-xs text-zinc-500">Start</Label>
              <Input id="start" type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="filter-start" />
            </div>
            <div>
              <Label htmlFor="end" className="text-xs text-zinc-500">End</Label>
              <Input id="end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="filter-end" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Club</Label>
              <Select value={clubId} onValueChange={setClubId}>
                <SelectTrigger data-testid="filter-club"><SelectValue placeholder="All clubs" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All clubs</SelectItem>
                  {clubs.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Player</Label>
              <Select value={playerId} onValueChange={setPlayerId}>
                <SelectTrigger data-testid="filter-player"><SelectValue placeholder="All players" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All players</SelectItem>
                  {players.map((pl) => (
                    <SelectItem key={pl.user_id} value={pl.user_id}>
                      {pl.display_name}{pl.username ? ` (@${pl.username})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Host Role</Label>
              <Select value={hostRole} onValueChange={setHostRole}>
                <SelectTrigger data-testid="filter-host-role"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="primary_host">Primary host</SelectItem>
                  <SelectItem value="co_host">Co-host</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-3 lg:col-span-2">
              <Label className="text-xs text-zinc-500">Options</Label>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 min-h-10">
                <label htmlFor="upcoming" className="flex items-center gap-2 text-sm text-zinc-600 cursor-pointer">
                  <input id="upcoming" type="checkbox" checked={includeUpcoming} onChange={(e) => setIncludeUpcoming(e.target.checked)} data-testid="filter-upcoming" className="h-4 w-4" />
                  <span>Include upcoming</span>
                </label>
                <label htmlFor="cancelled" className="flex items-center gap-2 text-sm text-zinc-600 cursor-pointer">
                  <input id="cancelled" type="checkbox" checked={includeCancelled} onChange={(e) => setIncludeCancelled(e.target.checked)} data-testid="filter-cancelled" className="h-4 w-4" />
                  <span>Include canceled</span>
                </label>
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={fetchReport} disabled={loading} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="apply-filters-btn">
              {loading ? 'Loading...' : 'Apply'}
            </Button>
            <Button variant="outline" onClick={exportCsv} disabled={!rows.length} data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Summary */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3" data-testid="report-summary">
            {[
              ['Sessions', summary.sessions],
              ['Completed', summary.completed_sessions],
              ['Canceled', summary.cancelled_sessions],
              ['Fill Rate', fmtOptionalPercent(summary.fill_rate)],
              ['Unique Players', summary.unique_players],
              ['Repeat Players', summary.repeat_players],
              ['Joined', summary.joined_count ?? summary.rostered_player_sessions],
              ['Waitlist', summary.waitlist_count],
            ].map(([label, val]) => (
              <div key={label} className="bg-white rounded-xl border border-zinc-200 p-4">
                <div className="text-xl font-bold text-zinc-900">{val ?? 0}</div>
                <div className="text-xs text-zinc-500">{label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Session Summary */}
        <div className="bg-white rounded-xl border border-zinc-200 overflow-x-auto" data-testid="session-summary-table">
          <div className="px-4 py-3 border-b border-zinc-100">
            <h2 className="text-sm font-semibold text-zinc-900">Session Summary</h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-zinc-500 border-b border-zinc-100">
                <th className="px-3 py-2">Session</th>
                <th className="px-3 py-2">Host Role</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Club</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Capacity</th>
                <th className="px-3 py-2">Joined</th>
                <th className="px-3 py-2">Confirmed</th>
                <th className="px-3 py-2">Waitlist</th>
                <th className="px-3 py-2">Fill %</th>
              </tr>
            </thead>
            <tbody>
              {sessionRows.length === 0 && (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-zinc-400">No sessions for this range.</td></tr>
              )}
              {sessionRows.map((r, i) => (
                <tr key={r.session_id || i} className="border-b border-zinc-50 hover:bg-zinc-50" data-testid="session-summary-row">
                  <td className="px-3 py-2 font-medium text-zinc-800">{r.session_title}</td>
                  <td className="px-3 py-2 text-zinc-600 whitespace-nowrap">{fmtHostRole(r.host_role)}</td>
                  <td className="px-3 py-2 text-zinc-600 whitespace-nowrap">{fmtDate(r.date_time_utc)}</td>
                  <td className="px-3 py-2 text-zinc-600">{r.club_name || EMPTY_VALUE}</td>
                  <td className="px-3 py-2 text-zinc-600">{r.status || EMPTY_VALUE}</td>
                  <td className="px-3 py-2 text-zinc-600">{r.capacity ?? EMPTY_VALUE}</td>
                  <td className="px-3 py-2 text-zinc-600">{r.joined_count ?? 0}</td>
                  <td className="px-3 py-2 text-zinc-600">{r.confirmed_count ?? 0}</td>
                  <td className="px-3 py-2 text-zinc-600">{r.waitlist_count ?? 0}</td>
                  <td className="px-3 py-2 text-zinc-600">{fmtOptionalPercent(r.fill_rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Roster Detail */}
        <div className="bg-white rounded-xl border border-zinc-200 overflow-x-auto" data-testid="report-table">
          <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-zinc-900">Roster Detail</h2>
              <p className="text-xs text-zinc-500">{rows.length} player rows</p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowRosterDetail((value) => !value)}
              data-testid="toggle-roster-detail"
            >
              {showRosterDetail ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
              {showRosterDetail ? 'Hide' : 'Show'}
            </Button>
          </div>
          {showRosterDetail && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-zinc-500 border-b border-zinc-100">
                  <th className="px-3 py-2">Session</th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Club</th>
                  <th className="px-3 py-2">Player</th>
                  <th className="px-3 py-2">Age</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Phone</th>
                  <th className="px-3 py-2">Roster</th>
                  <th className="px-3 py-2">Confirmed</th>
                  <th className="px-3 py-2">Billable</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={10} className="px-3 py-8 text-center text-zinc-400">No roster data for this range.</td></tr>
                )}
                {rows.map((r, i) => (
                  <tr key={i} className="border-b border-zinc-50 hover:bg-zinc-50" data-testid="report-row">
                    <td className="px-3 py-2 font-medium text-zinc-800">{r.session_title}</td>
                    <td className="px-3 py-2 text-zinc-600 whitespace-nowrap">{fmtDate(r.date_time_utc)}</td>
                    <td className="px-3 py-2 text-zinc-600">{r.club_name || EMPTY_VALUE}</td>
                    <td className="px-3 py-2 text-zinc-800">{r.player_display_name}</td>
                    <td className="px-3 py-2 text-zinc-600">{fmtAge(r.player_age)}</td>
                    <td className="px-3 py-2 text-zinc-600">{r.contact_redacted_minor ? <span className="text-amber-600 text-xs">redacted (minor)</span> : (r.player_email || EMPTY_VALUE)}</td>
                    <td className="px-3 py-2 text-zinc-600">{r.contact_redacted_minor ? <span className="text-amber-600 text-xs">redacted</span> : (r.player_phone || EMPTY_VALUE)}</td>
                    <td className="px-3 py-2 text-zinc-600">{fmtRosterStatus(r.roster_status)}</td>
                    <td className="px-3 py-2">{r.confirmation_status === 'confirmed' ? <span className="text-green-600">Yes</span> : <span className="text-zinc-400">No</span>}</td>
                    <td className="px-3 py-2">{r.billable ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <p className="text-xs text-zinc-400">
          Contact details are shown only for your own runs and are redacted for players under 18.
        </p>
      </div>
      <Footer variant="slim" />
    </div>
  );
};

export default HostReports;
